import { useState, useCallback } from 'react';
import chatbotService, { Chatbot, CreateChatbotRequest, UpdateChatbotRequest } from '../services/chatbotService';

export const useChatbots = () => {
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChatbots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const chatbots = await chatbotService.getChatbots();
      setChatbots(chatbots);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch chatbots');
    } finally {
      setLoading(false);
    }
  }, []);

  const createChatbot = useCallback(async (data: CreateChatbotRequest) => {
    setLoading(true);
    setError(null);
    try {
      const newChatbot = await chatbotService.createChatbot(data);
      setChatbots(prev => [...prev, newChatbot]);
      return newChatbot;
    } catch (err: any) {
      setError(err.message || 'Failed to create chatbot');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateChatbot = useCallback(async (id: string, data: UpdateChatbotRequest) => {
    setLoading(true);
    setError(null);
    try {
      const updatedChatbot = await chatbotService.updateChatbot(id, data);
      setChatbots(prev => prev.map(chatbot => chatbot.id === id ? updatedChatbot : chatbot));
      return updatedChatbot;
    } catch (err: any) {
      setError(err.message || 'Failed to update chatbot');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteChatbot = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await chatbotService.deleteChatbot(id);
      setChatbots(prev => prev.filter(chatbot => chatbot.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete chatbot');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    chatbots,
    loading,
    error,
    fetchChatbots,
    createChatbot,
    updateChatbot,
    deleteChatbot
  };
};

export default useChatbots;
